/**
 * SP-3a — Intent-spine membership map.
 *
 * A message belongs to spine S iff its nearest spine-root ancestor is S, where
 * the walk STOPS at the first `is_spine_root` ancestor encountered (a nested
 * fork becomes a new owner for its own subtree). Membership is scoped to
 * source-card spine roots (`blueprint_intent` | `artifact`) — SP-3b generalized
 * this from blueprint-only when the artifact source kind landed.
 *
 * One authority: the processor calls this ONCE and stamps the result onto node
 * data; the layout pass only reads the flag.
 */
type Msg = { id: string; parent_ids?: string[]; metadata?: any };

export function computeIntentSpineMembers(
  messages: Msg[],
  byId: Map<string, Msg>,
): Map<string, string> {
  const out = new Map<string, string>();
  for (const m of messages) {
    let cur: Msg | undefined = m;
    let owner: string | null = null;
    const guard = new Set<string>();
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      if (cur.metadata?.is_spine_root === true) {
        owner = cur.id;
        break;
      }
      const pid = cur.parent_ids?.[0];
      cur = pid ? byId.get(pid) : undefined;
    }
    if (owner) {
      const o = byId.get(owner);
      const md: any = o?.metadata;
      // SP-3b — any source-card spine (blueprint_intent | artifact | future).
      const kind = md?.spawn_source_kind ?? md?.spawn_source;
      if (kind === 'blueprint_intent' || kind === 'artifact') out.set(m.id, owner);
    }
  }
  return out;
}
